import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Database, FileSpreadsheet, Fingerprint, RefreshCw, ShieldCheck, Sparkles, Users, Zap } from 'lucide-react';
import { AuroraBackdrop } from '../components/AuroraBackdrop';
import { BrandMark } from '../components/BrandMark';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
const features = [{
  icon: Database,
  title: 'Live school database',
  body: 'Every student you record is written straight to your secure cloud database and read back live.'
}, {
  icon: RefreshCw,
  title: 'Instant sync across devices',
  body: 'Changes appear immediately, so the same account sees the same records on every phone and computer.'
}, {
  icon: FileSpreadsheet,
  title: 'Formatted Excel reports',
  body: 'Export all students, or filter by class or gender, into a clean, bordered .xlsx workbook.'
}, {
  icon: Fingerprint,
  title: 'Two-layer security',
  body: 'Verified Google sign-in, then your own master password gate before the dashboard opens.'
}];
const floaters = [{
  icon: Users,
  className: 'left-[6%] top-[22%]',
  delay: 0
}, {
  icon: FileSpreadsheet,
  className: 'right-[8%] top-[18%]',
  delay: 0.6
}, {
  icon: ShieldCheck,
  className: 'left-[12%] bottom-[16%]',
  delay: 1.1
}, {
  icon: Zap,
  className: 'right-[12%] bottom-[22%]',
  delay: 1.6
}];
export function Landing() {
  const navigate = useNavigate();
  const {
    user,
    unlocked,
    signInError
  } = useAuth();
  const goNext = () => navigate(user ? unlocked ? '/dashboard' : '/security' : '/security');
  return <div className="relative min-h-screen w-full overflow-hidden" data-id="element-540" data-mp="14p0qlwryn8s0">
      <AuroraBackdrop intensity="marketing" data-id="element-541" data-mp="14qbudwleogj4" />

      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6" data-id="element-542" data-mp="zwr64w9m70x4">
        <BrandMark size="sm" data-id="element-543" data-mp="1iv2ik9a5h04y" />
        <div className="flex items-center gap-2" data-id="element-544" data-mp="1iuszl5nivt8r">
          <ThemeToggle data-id="element-545" data-mp="1ntnliggsdxx3" />
          {user ? <button type="button" onClick={goNext} className="glass-flat inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-sm font-medium text-foreground" data-id="element-546" data-mp="1nscco28hhzz4">
              Continue
              <ArrowRight className="h-3.5 w-3.5" data-id="element-547" data-mp="10308jgs0ki3o" />
            </button> : null}
        </div>
      </header>

      {/* Floating decorations */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block" data-id="element-548" data-mp="153xqptd0xc56">
        {floaters.map((floater, index) => <motion.span key={index} animate={{
        y: [0, -18, 0]
      }} transition={{
        duration: 6 + index,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: floater.delay
      }} className={`glass absolute grid h-14 w-14 place-items-center rounded-2xl text-brand ${floater.className}`} data-id="element-549" data-mp="v46wlssdkz5m" data-mpi={index}>
            <floater.icon className="h-6 w-6" strokeWidth={1.7} data-id="element-550" data-mp="dw5vnsiq0i66" />
          </motion.span>)}
      </div>

      <main className="relative mx-auto w-full max-w-6xl px-5 pb-24" data-id="element-551" data-mp="9uu6fo3rdyyi">
        <section className="flex flex-col items-center pt-10 text-center sm:pt-16" data-id="element-552" data-mp="nl7bnip9mzk5">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="glass-flat inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium text-muted-foreground" data-id="element-553" data-mp="iuh5i390msoe">
            <Sparkles className="h-3.5 w-3.5 text-brand" data-id="element-554" data-mp="16jjkt4g6g9fq" />
            Built for real schools — no demo data, ever
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.06
        }} className="mt-6 max-w-4xl text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-6xl" data-id="element-555" data-mp="11m06k4jhbwns">
            The calm way to record <span className="gradient-text" data-id="element-556" data-mp="35qgw5zceelq2">every student</span> in your school
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.12
        }} className="mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg" data-id="element-557" data-mp="39qiovj05o4t7">
            Student Recorder keeps names, classes, parents, telephone numbers and notes in one secure cloud database —
            searchable in seconds and exportable to a polished Excel report whenever you need it.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.18
        }} className="mt-9 flex flex-col items-center gap-3 sm:flex-row" data-id="element-558" data-mp="2vzm7koboja1g">
            {user ? <button type="button" onClick={goNext} className="inline-flex h-12 items-center gap-2 rounded-2xl brand-gradient px-7 text-sm font-semibold text-white shadow-xl shadow-indigo-500/30 transition-transform hover:-translate-y-0.5" data-id="element-559" data-mp="28a1lvxo55yoh">
                Get Started
                <ArrowRight className="h-4 w-4" data-id="element-560" data-mp="1qvgwzjwekoqm" />
              </button> : <GoogleSignInButton label="Get Started with Google" data-id="element-561" data-mp="1lwnibo5dqrov" />}
            <p className="text-xs text-muted-foreground" data-id="element-562" data-mp="1huir0w2em75e">Google sign-in + your own master password</p>
          </motion.div>

          {signInError && <div role="alert" className="mt-5 flex max-w-xl items-start gap-2.5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-left text-sm" data-id="element-563" data-mp="182wgl1xw4c3p">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" data-id="element-564" data-mp="y5i39b79k3fm" />
              <p className="text-muted-foreground" data-id="element-565" data-mp="u3d98bcwl4sg">{signInError}</p>
            </div>}
        </section>

        <motion.section initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.24
      }} className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Product highlights" data-id="element-566" data-mp="jhrtv87qf9t3">
          {features.map(feature => <article key={feature.title} className="glass rounded-3xl p-5 text-left" data-id="element-567" data-mp="fhpp2q6pa09l">
              <span className="grid h-11 w-11 place-items-center rounded-2xl brand-gradient-soft text-brand" data-id="element-568" data-mp="1360m1i99zfsl">
                <feature.icon className="h-5 w-5" strokeWidth={1.8} data-id="element-569" data-mp="176svabi5dkov" />
              </span>
              <h2 className="mt-4 text-sm font-semibold tracking-tight text-foreground" data-id="element-570" data-mp="th8wfgrot7ku">{feature.title}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground" data-id="element-571" data-mp="yeilgvb5zh85">{feature.body}</p>
            </article>)}
        </motion.section>

        <motion.section initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="glass mt-6 flex flex-col items-center gap-6 rounded-3xl p-7 text-center sm:flex-row sm:justify-between sm:p-9 sm:text-left" data-id="element-572" data-mp="1qvuxbvdy4waq">
          <div className="max-w-xl" data-id="element-573" data-mp="2a0zqfh8zw9qd">
            <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl" data-id="element-574" data-mp="29zydf78f5d60">
              Start with an empty register — exactly as it should be
            </h2>
            <p className="mt-2 text-sm text-muted-foreground" data-id="element-575" data-mp="1hcu4wsa4agph">
              No sample data, no placeholders. The first student in your dashboard will be one you recorded yourself.
            </p>
          </div>
          {user ? <button type="button" onClick={goNext} className="inline-flex h-12 shrink-0 items-center gap-2 rounded-2xl brand-gradient px-7 text-sm font-semibold text-white shadow-xl shadow-indigo-500/30" data-id="element-576" data-mp="1rb9xkzosly13">
              Open dashboard
              <ArrowRight className="h-4 w-4" data-id="element-577" data-mp="2xpj123fioylm" />
            </button> : <GoogleSignInButton className="shrink-0" data-id="element-578" data-mp="w269skgbk2po" />}
        </motion.section>
      </main>

      <footer className="mx-auto w-full max-w-6xl px-5 pb-10 text-center text-xs text-muted-foreground" data-id="element-579" data-mp="1039rlchxg8tj">
        <p data-id="element-580" data-mp="15006vxlwr0dw">
          Student Recorder — crafted by <span className="font-medium text-foreground" data-id="element-581" data-mp="19s9u35vn1mch">Paulin Dev Studio</span>. Secure
          Google sign-in, encrypted cloud storage and instant sync.
        </p>
      </footer>
    </div>;
}